// Anke, 2026-07-16 — der Dreh-Anfasser soll ein Punkt mit einem Kreispfeil
// drumherum sein, statt eines Quadrats wie die vier Eck-Anfasser.
//
// Konva erzeugt den Dreh-Anfasser selbst, als `Konva.Rect` mit dem Namen
// `rotater _anchor` (Transformer.js). Wir ersetzen ihn daher NICHT durch einen
// eigenen Knoten — der Transformer haengt seine gesamte Drehlogik an genau
// diesen Knoten. Stattdessen uebernehmen wir nur sein Aussehen ueber
// `anchorStyleFunc`: Konva ruft das am ENDE jedes `update()` fuer jeden Anker
// auf, also nach seiner eigenen Anker-Formatierung. Was wir hier setzen,
// gewinnt und ueberlebt jedes Auffrischen.

import Konva from 'konva';
import { HANDLE_STROKE } from '../../constants/whiteboard-config.constants.js';

/**
 * Kantenlaenge des Dreh-Anfasser-Kaestchens. Deutlich groesser als
 * HANDLE_SIZE_PX (10), weil hier ein Kreispfeil mit Punkt hineinpasst, nicht
 * nur eine Flaeche — bei 10 waere der Pfeil nicht mehr zu erkennen.
 *
 * Anke, 2026-07-16: von 16 auf 24 (rund das 1,5-fache) angehoben. Konva
 * zentriert den Anker ueber `offsetX/offsetY = size / 2`, die Mitte bleibt also
 * da, wo sie war: Der groessere Kasten verschiebt den Drehpunkt nicht.
 */
export const ROTATE_ANCHOR_SIZE_PX = 24;

/** Farbe von Punkt und Kreispfeil. Wie die Anfasser-Kontur, damit der
 *  Dreh-Anfasser zur restlichen Auswahl-Optik gehoert. */
const GLYPH_COLOR = HANDLE_STROKE;

/** Trifft genau Konvas Dreh-Anker (`name = 'rotater _anchor'`). */
export function isRotateAnchor(node: Konva.Node): boolean {
  return node.name().split(/\s+/).includes('rotater');
}

/** Radius der weissen Kreisscheibe — Zeichnung und Trefferflaeche teilen ihn. */
function badgeRadius(shape: Konva.Shape): number {
  return Math.min(shape.width(), shape.height()) / 2 - 1;
}

/**
 * Zeichnet Punkt + Kreispfeil in das Kaestchen des Ankers.
 *
 * Der weisse Kreis darunter ist Absicht und nicht nur Zierde: Er hebt das
 * Zeichen von beliebigem Board-Inhalt ab (genau wie die weissen Quadrate der
 * Eck-Anfasser) UND er ist die Flaeche, die `fill()` traegt. Dadurch wirkt die
 * bestehende Hover-Einfaerbung (`wireTransformerAnchorHover`) hier weiter, ohne
 * dass sie den Dreh-Anfasser kennen muss.
 */
function drawRotateGlyph(ctx: Konva.Context, shape: Konva.Shape): void {
  const cx = shape.width() / 2;
  const cy = shape.height() / 2;
  const badge = badgeRadius(shape);

  // Unterlage: nimmt fill()/stroke() des Ankers → Hover faerbt weiterhin.
  ctx.beginPath();
  ctx.arc(cx, cy, badge, 0, Math.PI * 2, false);
  ctx.closePath();
  ctx.fillStrokeShape(shape);

  const ring = badge * 0.55;
  const gap = Math.PI * 0.42; // Luecke, in der der Pfeilkopf sitzt
  const end = -Math.PI / 2 - gap / 2; // Ende oben, leicht nach links versetzt

  ctx.beginPath();
  ctx.arc(cx, cy, ring, -Math.PI / 2 + gap / 2, end + Math.PI * 2, false);
  ctx.setAttr('strokeStyle', GLYPH_COLOR);
  ctx.setAttr('lineWidth', 1.2);
  ctx.setAttr('lineCap', 'round');
  ctx.stroke();

  // Pfeilkopf am Ende des Bogens, tangential ausgerichtet.
  const hx = cx + Math.cos(end) * ring;
  const hy = cy + Math.sin(end) * ring;
  const head = badge * 0.34;
  const tangent = end + Math.PI / 2; // Laufrichtung des Bogens am Endpunkt
  ctx.beginPath();
  ctx.moveTo(hx + Math.cos(tangent) * head, hy + Math.sin(tangent) * head);
  ctx.lineTo(hx + Math.cos(tangent + 2.4) * head, hy + Math.sin(tangent + 2.4) * head);
  ctx.lineTo(hx + Math.cos(tangent - 2.4) * head, hy + Math.sin(tangent - 2.4) * head);
  ctx.closePath();
  ctx.setAttr('fillStyle', GLYPH_COLOR);
  ctx.fill();

  // Der Punkt in der Mitte — das ist der Drehpunkt.
  ctx.beginPath();
  ctx.arc(cx, cy, Math.max(1, badge * 0.16), 0, Math.PI * 2, false);
  ctx.closePath();
  ctx.setAttr('fillStyle', GLYPH_COLOR);
  ctx.fill();
}

/**
 * Trefferflaeche = genau die sichtbare Kreisscheibe samt Rand, ohne Zusatzrand
 * (Anke, 2026-09-29; vorher 4 Einheiten ueber das Kaestchen hinaus). Eine eigene
 * hitFunc bleibt trotzdem noetig: Ohne sie faellt Konva beim Treffer-Test auf
 * die Zeichenfunktion zurueck (`Shape.drawHit`: `hitFunc() || sceneFunc()`),
 * und greifbar waere nur der duenne Pfeilstrich.
 */
function drawRotateHit(ctx: Konva.Context, shape: Konva.Shape): void {
  const cx = shape.width() / 2;
  const cy = shape.height() / 2;
  ctx.beginPath();
  ctx.arc(cx, cy, badgeRadius(shape), 0, Math.PI * 2, false);
  ctx.closePath();
  ctx.fillStrokeShape(shape);
}

/**
 * Verwandelt Konvas quadratischen Dreh-Anker in Punkt + Kreispfeil.
 *
 * `fill` wird bewusst NICHT gesetzt: Konva schreibt bei jedem `update()`
 * `anchorFill` auf alle Anker, und ein zusaetzliches Setzen hier wuerde eine
 * gerade aktive Hover-Farbe ueberschreiben. Die Zeichenfunktion liest `fill()`
 * zur Laufzeit, damit bleibt Hover unberuehrt.
 */
export function styleRotateAnchor(anchor: Konva.Shape): void {
  anchor.width(ROTATE_ANCHOR_SIZE_PX);
  anchor.height(ROTATE_ANCHOR_SIZE_PX);
  anchor.offsetX(ROTATE_ANCHOR_SIZE_PX / 2);
  anchor.offsetY(ROTATE_ANCHOR_SIZE_PX / 2);
  anchor.sceneFunc(drawRotateGlyph);
  anchor.hitFunc(drawRotateHit);
}
